<template>
  <div class="conic-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ topicFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 渲染舞台 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="360" viewBox="-150 -120 300 240" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="conic-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
           </defs>
           
            <!-- 背景网格: 极坐标模式下切换为极网格 -->
            <g v-if="mode === 'polar'" stroke="rgba(255,255,255,0.08)" stroke-width="1">
              <circle v-for="rIdx in [25, 50, 75, 100, 125]" :key="'p'+rIdx" cx="0" cy="0" :r="rIdx" fill="none" />
              <line v-for="aIdx in 12" :key="'a'+aIdx" x1="0" y1="0" :x2="Math.cos(aIdx*Math.PI/6)*150" :y2="Math.sin(aIdx*Math.PI/6)*150" />
            </g>
            <g v-else stroke="rgba(255,255,255,0.05)" stroke-width="1">
              <line v-for="i in 11" :key="'v'+i" :x1="(i-6)*25" y1="-120" :x2="(i-6)*25" y2="120" />
              <line v-for="i in 9" :key="'h'+i" x1="-150" :y1="(i-5)*25" x2="150" :y2="(i-5)*25" />
            </g>
            <line v-if="mode !== 'polar'" x1="-150" y1="0" x2="150" y2="0" stroke="#64748b" stroke-width="2"/> <!-- X Axis -->
            <line v-if="mode !== 'polar'" x1="0" y1="-120" x2="0" y2="120" stroke="#64748b" stroke-width="2"/> <!-- Y Axis -->
            <line v-if="mode === 'polar'" x1="0" y1="0" x2="140" y2="0" stroke="#fbbf24" stroke-width="3" /> <!-- Polar Axis -->
            <text v-if="mode === 'polar'" x="145" y="15" fill="#fbbf24" font-size="12">极轴 Ox</text>

            <!-- 焦点与准线模式 (笛卡尔) -->
            <g v-if="mode === 'geometry' || mode === 'equation'">
              <g v-if="eVal > 0">
                <circle :cx="-cVal" cy="0" r="4" fill="#fbbf24" />
                <circle :cx="cVal" cy="0" r="4" fill="#fbbf24" />
                <text :x="cVal+5" y="-10" fill="#fbbf24" font-size="12" font-weight="bold">F₂</text>
                <text :x="-cVal-18" y="-10" fill="#fbbf24" font-size="12" font-weight="bold">F₁</text>
              </g>
              <g v-else>
                <circle cx="0" cy="0" r="5" fill="#fcd34d" />
                <text x="5" y="-8" fill="#fcd34d" font-size="14" font-weight="bold">O</text>
              </g>
              
              <!-- 连线 (椭圆/双曲线的距离之和/差) -->
              <g v-if="mode === 'geometry' && pts && pts.length > 10">
                <line v-if="eVal > 0" :x1="-cVal" y1="0" :x2="pPoint.x" :y2="pPoint.y" stroke="#38bdf8" stroke-dasharray="3,3" />
                <line :x1="cVal" y1="0" :x2="pPoint.x" :y2="pPoint.y" stroke="#38bdf8" stroke-dasharray="3,3" />
                <circle :cx="pPoint.x" :cy="pPoint.y" r="4" fill="#38bdf8"/>
                <text :x="pPoint.x-5" :y="pPoint.y-10" fill="#38bdf8" font-size="12" font-weight="bold">P</text>
              </g>
            </g>

           <!-- 关系模式: 直线与圆 -->
           <g v-if="mode === 'relation'">
             <circle cx="0" cy="0" :r="aVal" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1.5" stroke-dasharray="8,6" />
             <line x1="-150" :y1="relationDist" x2="150" :y2="relationDist" stroke="#38bdf8" stroke-width="3" filter="url(#conic-glow)" />
             <text x="110" :y="relationDist-10" fill="#38bdf8" font-size="12">直线 l</text>
             <text x="0" y="5" fill="#fff" text-anchor="middle" font-size="24" font-weight="900" style="opacity:0.2">O</text>
           </g>

            <!-- 极坐标动态点标注 -->
            <g v-if="mode === 'polar' && pts && pts.length > 50">
              <line x1="0" y1="0" :x2="pPoint.x" :y2="pPoint.y" stroke="#fbbf24" stroke-dasharray="4,3" />
              <text :x="pPoint.x/2+5" :y="pPoint.y/2-5" fill="#fbbf24" font-size="12">ρ</text>
              <path :d="polarAnglePath" fill="none" stroke="#fbbf24" stroke-width="1.5" />
              <text x="35" y="-10" fill="#fbbf24" font-size="13">θ</text>
              <circle :cx="pPoint.x" :cy="pPoint.y" r="5" fill="#f43f5e" />
              <text :x="pPoint.x+10" :y="pPoint.y-10" fill="#fff" font-size="14" font-weight="bold">P(ρ, θ)</text>
            </g>

           <!-- 曲线主体 -->
           <polyline v-if="mode !== 'relation'" :points="curvePoints" fill="none" :stroke="colorTheme" stroke-width="3.5" filter="url(#conic-glow)" />
           <polyline v-if="eVal > 1 && mode !== 'relation'" :points="hyperbolaLeft" fill="none" :stroke="colorTheme" stroke-width="3.5" filter="url(#conic-glow)" />
           
        </svg>

        <div class="hud-panel">
            <div class="hud-item primary">
               <label v-if="mode === 'polar'">极径 ρ</label>
               <label v-else-if="mode === 'relation'">圆半径 r</label>
               <label v-else>离心率 e</label>
               <span class="val">{{ mode === 'polar' ? pRadius.toFixed(1) : (mode === 'relation' ? aVal.toFixed(0) : parseFloat(eVal).toFixed(2)) }}</span>
            </div>
            <div class="hud-item secondary" v-if="mode !== 'eccentricity'">
              <label v-if="mode !== 'relation'">焦距 2c</label><label v-else>圆心距 d</label>
              <span class="val">{{ mode === 'relation' ? Math.abs(relationDist).toFixed(0) : (2 * cVal).toFixed(1) }}</span>
            </div>
        </div>
      </div>

      <!-- 右侧概念映射区域 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">{{ abstractTitle }}</div>
         
         <div class="flex-column-center" style="flex:1; padding: 20px;">
            <!-- Mode 1: 方程解析 -->
            <div v-if="mode === 'equation'" class="eq-box text-center">
               <div v-if="eVal == 0" class="eq-display text-green" style="font-size: 24px;">
                  x² + y² = {{ (aVal*aVal).toFixed(0) }}
               </div>
               <div v-else-if="eVal < 1" class="eq-display text-green">
                  x² / {{ (aVal*aVal).toFixed(0) }} + y² / {{ ((aVal*aVal)*(1-eVal*eVal)).toFixed(0) }} = 1
               </div>
               <div v-else-if="eVal === 1" class="eq-display text-blue">
                  y² = {{ (2*aVal).toFixed(0) }} x
               </div>
               <div v-else class="eq-display text-red">
                  x² / {{ (aVal*aVal).toFixed(0) }} - y² / {{ ((aVal*aVal)*(eVal*eVal-1)).toFixed(0) }} = 1
               </div>
               <p v-if="eVal == 0" class="desc-text mt-4">当圆锥曲线的离心率 e 为 0 时，两个焦点 F₁ 和 F₂ 重合于坐标原点，即形成完美的圆。标准方程简化为 x² + y² = r²。</p>
               <p v-else class="desc-text mt-4">二次方程通过系数值决定了它在直角坐标系中的形态。当x²与y²系数同号则为椭圆，差号则为双曲线，缺项则为抛物线。</p>
            </div>

            <!-- Mode 2: 几何性质 -->
            <div v-if="mode === 'geometry'" class="eq-box">
               <div class="geo-stat" v-if="eVal < 1">|PF₁| + |PF₂| = 2a = {{ (2*aVal).toFixed(0) }}</div>
               <div class="geo-stat" v-else-if="eVal > 1">||PF₁| - |PF₂|| = 2a = {{ (2*aVal).toFixed(0) }}</div>
               <div class="geo-stat" v-else>|PF| = d (到准线距离)</div>
               <p class="desc-text mt-4">在这里，动点 P 到两个定点（焦点）的距离关系的恒定，是圆锥曲线的几何本质。</p>
            </div>

            <!-- Mode 3: 截面圆锥形态 -->
            <div v-if="mode === 'eccentricity'" class="cone-svg-box">
               <svg width="150" height="200" viewBox="0 0 100 150">
                  <path d="M50 75 L10 10 L90 10 Z" fill="rgba(255,255,255,0.05)" stroke="#64748b" stroke-width="1.5"/>
                  <path d="M50 75 L10 140 L90 140 Z" fill="rgba(255,255,255,0.05)" stroke="#64748b" stroke-width="1.5"/>
                  <!-- 动态截面绘制 -->
                  <line x1="20" :y1="100 - eVal*30" x2="80" :y2="100 + eVal*30" stroke="#f43f5e" stroke-width="4" />
               </svg>
               <p class="desc-text text-center">用平面切割双圆锥：调整 e 值即是改变切面倾角。当它和母线平行时 e=1 (抛物线)。</p>
            </div>

            <!-- Mode 4: 位置关系展示 -->
            <div v-if="mode === 'relation'" class="eq-box text-center">
               <div class="eq-display text-blue" style="box-shadow: 0 0 20px rgba(56, 189, 248, 0.2)">
                 {{ relationStatus }}
               </div>
               <p class="desc-text mt-4">当圆心到直线的距离 d > r 时相离，d = r 时相切，d < r 时相交。</p>
            </div>

            <!-- Mode 6: 参数方程 -->
            <div v-if="mode === 'parametric'" class="eq-box text-center">
               <div class="eq-display text-blue">
                 x = {{ aVal }}·cos t <br/>
                 y = {{ (aVal*Math.sqrt(Math.abs(1-eVal*eVal))).toFixed(0) }}·sin t
               </div>
               <p class="desc-text mt-4">参数方程通过引入中介参数 t，将几何形状解构为两个独立的坐标分量。它在物理学中常用来刻画随时间变化的物体位置。</p>
            </div>

            <!-- Mode 5: 极坐标方程内容 -->
            <div v-if="mode === 'polar'" class="eq-box text-center">
               <div class="eq-display text-yellow">
                 ρ = {{ aVal }} / (1 - {{ parseFloat(eVal).toFixed(2) }}·cosθ)
               </div>
               <p class="desc-text mt-4">极坐标系通过径长与角度唯一确定点。圆锥曲线的统一定义在极坐标下表现为简明的线性比率关系。</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
        <div class="control-grid-mini">
           <div class="s-item">
              <label>离心形态 e (当前: {{ curveTypeName }})</label>
              <el-slider v-model="eVal" :min="0" :max="2.5" :step="0.05" size="small"></el-slider>
           </div>
           <div class="s-item">
              <label>{{ mode === 'polar' ? '焦准距 p' : '焦点基准参数 a' }}</label>
              <el-slider v-model="aVal" :min="20" :max="90" size="small"></el-slider>
           </div>
        </div>
      </div>

      <div class="discovery-card-compact" v-latex>
         <i class="el-icon-data-board" style="color:#0ea5e9;"></i>
         <div class="discovery-content">
            <p><strong>{{ tipTitle }}：</strong>{{ tipDesc }}</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConicSectionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'equation',
      eVal: 0.8,
      aVal: 60,
      pts: [] // for geometric rendering access
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    },
    eVal() { this.updatePts(); },
    aVal() { this.updatePts(); }
  },
  computed: {
    cVal() { return this.eVal * this.aVal; },
    pPoint() {
      if (this.pts && this.pts.length > 10) {
        const parts = this.pts[10].split(',');
        if (parts.length === 2) {
          return { x: parseFloat(parts[0]), y: parseFloat(parts[1]) };
        }
      }
      return { x: 0, y: 0 };
    },
    curveTypeName() {
      if (this.eVal == 0) return '圆';
      if (this.eVal < 1) return '椭圆';
      if (this.eVal == 1) return '抛物线';
      return '双曲线';
    },
    topicDesc() {
      if (this.eVal == 0 && this.mode === 'equation') return '圆的标准方程：r 为半径，(0,0) 为圆心';
      if (this.mode === 'equation') return '圆锥曲线的标准方程及其参数映射逻辑';
      if (this.mode === 'geometry') return '第一/第二定义的几何性质与动点轨迹探索';
      if (this.mode === 'relation') return '直线与圆的位置关系：d 与 r 的动态判别';
      if (this.mode === 'polar') return '极坐标方程：r = ep / (1 ± e·cosθ)';
      if (this.mode === 'parametric') return '参数方程的应用：x=acos t, y=bsin t';
      return '离心率（e）作为圆锥曲线形貌刻画的统一度量';
    },
    topicFormula() {
      if (this.eVal == 0 && this.mode === 'equation') return 'x² + y² = r²';
      if (this.mode === 'equation') return 'x²/a² ± y²/b² = 1';
      if (this.mode === 'geometry') return '|PF₁| ± |PF₂| = 2a';
      if (this.mode === 'relation') return 'd = |Ax₀+By₀+C|/√(A²+B²)';
      if (this.mode === 'polar') return 'ρ = f(θ)';
      if (this.mode === 'parametric') return 'x=acos t, y=bsin t';
      return 'e = c/a';
    },
    abstractTitle() {
      if (this.eVal == 0 && this.mode === 'equation') return '圆方程解析板';
      if (this.mode === 'equation') return '代数参数解析板';
      if (this.mode === 'geometry') return '轨迹特征分析器';
      if (this.mode === 'relation') return '位置关系判别';
      if (this.mode === 'polar') return '极坐标解析式表现';
      if (this.mode === 'parametric') return '参数方程映射逻辑';
      return '立体圆锥截面投影';
    },
    polarAnglePath() {
       const ang = Math.atan2(this.pPoint.y, this.pPoint.x);
       let endX = Math.cos(ang) * 30;
       let endY = Math.sin(ang) * 30;
       return `M 30 0 A 30 30 0 ${ang < 0 ? 0 : 0} 1 ${endX} ${endY}`;
    },
    pRadius() {
       return Math.sqrt(this.pPoint.x**2 + this.pPoint.y**2);
    },
    relationDist() {
      // 用于位置关系模式的动态直线高度
      return (this.eVal - 0.5) * 200; 
    },
    relationStatus() {
       const d = Math.abs(this.relationDist);
       if (d > this.aVal + 2) return '相离 (d > r)';
       if (d < this.aVal - 2) return '相交 (d < r)';
       return '相切 (d = r)';
    },
    colorTheme() {
      if (this.eVal < 1) return '#34d399';
      if (this.eVal === 1) return '#38bdf8';
      return '#fb7185';
    },
    curvePoints() {
      return this.pts.join(' ');
    },
    hyperbolaLeft() {
      let pts = [];
      const a = this.aVal;
      const e = this.eVal;
      const b = a * Math.sqrt(e*e - 1);
      for (let t = -1.3; t <= 1.3; t += 0.05) {
         pts.push(`${-a / Math.cos(t)},${-b * Math.tan(t)}`);
      }
      return pts.join(' ');
    },
    tipTitle() {
      return this.mode === 'polar' ? '坐标魅力' : (this.mode === 'equation' ? '代数之美' : (this.mode === 'geometry' ? '几何之美' : '统一之美'));
    },
    tipDesc() {
      if (this.mode === 'polar') return '在极坐标的世界里，一切复杂的曲线都可以用角度与半径的简洁关系来表达。';
      if (this.mode === 'equation') return '圆锥曲线的代数方程完美地揭示了它的对称性和渐近趋势，a与b的值决定了它的胖瘦与开口宽窄。';
      if (this.mode === 'geometry') return '动点 P 到固定焦点的恒定关系构成了曲线。应用极广，例如卫星轨道（椭圆）与汽车前照灯反光碗（抛物线）。';
      if (this.mode === 'parametric') return '参数方程将二维坐标解构为两个时间的函数（或角度函数），是处理物理位移与传感器轨迹预测的利器。';
      return '所有的圆锥曲线都可看作是同一个立体沙漏被不同角度切开的剖面图。离心率 e 就像是这把刀的倾斜角度刻度。';
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      console.log('Detecting mode for:', kp);
      
      // Auto-set eccentricity based on curve type in knowledge point
      if (kp.includes('椭圆')) {
         this.eVal = 0.6;
      } else if (kp.includes('抛物线')) {
         this.eVal = 1.0;
      } else if (kp.includes('双曲线')) {
         this.eVal = 1.5;
      }

      if (kp.includes('位置关系')) {
         this.mode = 'relation';
         this.eVal = 0.5;
      } else if (kp.includes('极坐标')) {
         this.mode = 'polar';
         this.eVal = 0.6;
      } else if (kp.includes('参数')) {
         this.mode = 'parametric';
         this.eVal = 0.75;
      } else if (kp.includes('性质') || kp.includes('几何') || kp.includes('轨迹')) {
         this.mode = 'geometry';
      } else if (kp.includes('离心') || kp.includes('统一定义')) {
         this.mode = 'eccentricity';
         this.eVal = 1.0;
      } else if (kp.includes('圆的方程')) {
         this.mode = 'equation';
         this.eVal = 0;
      } else {
         this.mode = 'equation';
      }
      this.updatePts();
    },
    updatePts() {
      let pts = [];
      const a = this.aVal;
      const e = this.eVal;
      
      if (this.mode === 'polar') {
         const p = a;
         for (let t = 0; t <= Math.PI*2; t += 0.05) {
            const r = (e < 1 || Math.abs(1 - e*Math.cos(t)) > 0.1) ? (e*p) / (1 - e * Math.cos(t)) : 300;
            if (Math.abs(r) < 500) {
               pts.push(`${r * Math.cos(t)},${-r * Math.sin(t)}`);
            }
         }
      } else if (e < 1) { // 椭圆
         const b = a * Math.sqrt(1 - e*e);
         for (let t = 0; t <= Math.PI*2; t += 0.05) {
            pts.push(`${a * Math.cos(t)},${-b * Math.sin(t)}`);
         }
         pts.push(`${a},0`); 
      } else if (e === 1) { // 抛物线
         const p = a; 
         for (let y = -120; y <= 120; y += 4) {
            const x = (y*y)/(2*p);
            pts.push(`${x - a/2},${-y}`); 
         }
      } else { // 双曲线右支
         const b = a * Math.sqrt(e*e - 1);
         for (let t = -1.3; t <= 1.3; t += 0.05) {
            pts.push(`${a / Math.cos(t)},${-b * Math.tan(t)}`);
         }
      }
      this.pts = pts;
    }
  }
}
</script>

<style scoped lang="scss">
.conic-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; align-items: center; justify-content: center; }

.eq-box { width: 100%; }
.eq-display { font-size: 20px; font-weight: 900; font-family: monospace; letter-spacing: 1px; padding: 15px; border-radius: 8px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); }
.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #fb7185; } .text-yellow { color: #fbbf24; }
.geo-stat { font-size: 22px; font-weight: 900; color: #fbbf24; text-align: center; font-family: monospace; }
.desc-text { color: #94a3b8; font-size: 13px; line-height: 1.6; padding: 10px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.cone-svg-box { display: flex; flex-direction: column; align-items: center; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #1e293b; padding: 8px 16px; label { display: block; font-size: 12px; color: #94a3b8; font-weight: 900; } .val { font-size: 18px; color: #f8fafc; font-weight: 900; font-family: monospace; } &.primary { border-left: 4px solid #38bdf8; } &.secondary { border-left: 4px solid #8b5cf6; } } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
.discovery-card-compact { background: #f0f9ff; padding: 15px 20px; border-radius: 12px; border: 1px solid #bae6fd; display: flex; align-items: flex-start; gap: 15px; i { font-size: 1.4rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 14px; color: #0284c7; line-height: 1.6; strong { color: #075985; font-weight: 900; } } } }
</style>
